import React, { useState } from 'react'
import { Table, Button, Flex } from 'antd'
import { useLockBodyScroll } from 'react-use'
import { RightSquareFilled } from '@ant-design/icons'
import { FormattedMessage, injectIntl } from 'react-intl'
import { Input } from 'antd'
import { requireAuthentication } from '../../helpers/authentication'
import { useSearchDTCs } from '../../hooks/GlobalSearch'
import { useAddManualVehicleTest } from '../../hooks/ManualVehicleTest'
import { useHistory } from 'react-router-dom'
import { useSelector, shallowEqual } from 'react-redux'
import { generateTestPayload } from '../../services/test-payload'

import './styles.scss'

const GenerateVehicleTestButton = ({ vin, dtcs }) => {
  const user = useSelector((state) => state.user.data.username, shallowEqual)
  const addVehicleTest = useAddManualVehicleTest()
  const history = useHistory()

  const onClick = () => {
    const payload = generateTestPayload({ vin, userId: user, dtcs })
    addVehicleTest.mutate(
      { payload },
      {
        onSuccess: (data, variables, context) =>
          history.push(`/tracer/${data.cache_id}`),
      },
    )
  }

  return (
    <Button
      block
      disabled={dtcs.length === 0}
      onClick={onClick}
      size="large"
      type="primary"
      loading={addVehicleTest.isLoading}
    >
      <FormattedMessage id="buildYourTest.generateTest" />
    </Button>
  )
}

function BuildYourTestPage() {
  const [vin, setVin] = useState('')
  const [searchString, setSearchString] = useState(null)
  const [payloadDtcs, setPayloadDtcs] = useState([])
  const { isLoading, results: dtcs } = useSearchDTCs({ vin, searchString })

  useLockBodyScroll(true)

  const dtcId = (dtc) => `${dtc.acronym}-${dtc.name}`
  const addPayloaddDtc = (dtc) =>
    setPayloadDtcs([...new Set([...payloadDtcs, dtc])])
  const removePayloaddDtc = (dtc) => {
    setPayloadDtcs(
      payloadDtcs.filter((payloadDtc) => dtcId(payloadDtc) !== dtcId(dtc)),
    )
  }

  const columns = [
    {
      title: <FormattedMessage id="buildYourTest.columns.name" />,
      dataIndex: 'name',
    },
    {
      title: <FormattedMessage id="buildYourTest.columns.acronym" />,
      dataIndex: 'acronym',
    },
    {
      title: <FormattedMessage id="buildYourTest.columns.description" />,
      dataIndex: 'description',
    },
  ]
  const dtcColumns = [
    ...columns,
    {
      title: '',
      key: 'button',
      render: (text, record, index) => {
        const isAdded = payloadDtcs.some((dtc) => dtcId(dtc) === dtcId(record))
        return (
          <Button
            type="primary"
            disabled={isAdded}
            onClick={() => addPayloaddDtc(record)}
          >
            <FormattedMessage id="buildYourTest.columns.add" />
          </Button>
        )
      },
    },
  ]
  const payloadColumns = [
    ...columns,
    {
      title: '',
      key: 'button',
      render: (text, record, index) => (
        <Button type="primary" onClick={() => removePayloaddDtc(record)}>
          <FormattedMessage id="buildYourTest.columns.remove" />
        </Button>
      ),
    },
  ]

  return (
    <Flex vertical={true} id="build-your-test-page">
      <div
        style={{
          width: '100%',
          display: 'flex',
          marginTop: '20px',
          justifyContent: 'start',
        }}
      >
        <h2>
          <FormattedMessage id="buildYourTest.buildYourTest" />
        </h2>
      </div>
      <div
        style={{
          width: '48%',
          display: 'flex',
          marginTop: '20px',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ width: '40%', display: 'flex', flexDirection: 'column' }}>
          <div>
            <FormattedMessage id="buildYourTest.vin" />
          </div>
          <Input
            maxLength={17}
            placeholder="Enter VIN"
            onChange={(event) => setVin(event.target.value)}
          />
        </div>
        <div style={{ width: '40%' }}>
          <div>
            <FormattedMessage id="buildYourTest.dtc" />
          </div>
          <Input.Search
            placeholder="Search DTCs"
            enterButton="Search"
            onSearch={(value, event) => setSearchString(value)}
            size="medium"
            loading={isLoading}
            disabled={!(vin && vin.length === 17)}
          />
        </div>
      </div>
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '20px',
        }}
      >
        <div style={{ width: '48%' }}>
          <Table
            rowKey={(record) =>
              `${record.make_model_id}-${record.acronym}-${record.name}`
            }
            size="small"
            columns={dtcColumns}
            dataSource={dtcs}
            loading={isLoading}
            pagination={{
              pageSize: 8,
              hideOnSinglePage: true,
            }}
          />
        </div>
        <div
          style={{
            width: '4%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <RightSquareFilled style={{ fontSize: '24px' }} />
        </div>
        <div style={{ width: '48%' }}>
          <Table
            rowKey={(record) =>
              `${record.make_model_id}-${record.acronym}-${record.dtc}`
            }
            size="small"
            columns={payloadColumns}
            dataSource={payloadDtcs}
            pagination={{
              pageSize: 8,
              hideOnSinglePage: true,
            }}
          />
        </div>
      </div>
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px',
        }}
      >
        <div style={{ width: '20%' }}>
          <GenerateVehicleTestButton vin={vin} dtcs={payloadDtcs} />
        </div>
      </div>
    </Flex>
  )
}

export default requireAuthentication(injectIntl(BuildYourTestPage))
